// @flow

import * as React from 'react';

import type { Currency } from '@gandi/cart';
import { useTranslator, type TranslatorProps } from '@gandi/translate';
import { Base, Text } from '@gandi/ui';

import { useChangeText } from 'src/DomainTransfer/components/modules';

import styles from './PriceText.scss';

type Props = {
  currency: Currency,
  price: number,
  minDuration: number,
  renewPrice?: number,
  renewMinDuration?: number,
  priceDiff?: number,
  discountCheaperThanRenew?: boolean,
  needsPhaseSelection: boolean,
  durationChange?: 'unchanged' | 'renew' | 'reset' | string,
};

const priceText = (
  formattedPrice: string,
  needsPhaseSelection: boolean,
  __: TranslatorProps['__'],
): string =>
  needsPhaseSelection
    ? __('From %(price)s', { price: formattedPrice })
    : formattedPrice;

const durationText = (
  minDuration: number,
  __: TranslatorProps['__'],
  timeLimited?: number,
): string => {
  if (timeLimited) {
    return __('%(count)s year at', { count: minDuration });
  }

  return minDuration === 1
    ? __('/year')
    : __('/%(count)s years', { count: minDuration });
};

function NormalPrice({
  formattedPrice,
  needsPhaseSelection,
}: {
  formattedPrice: string,
  needsPhaseSelection: boolean,
}) {
  const { __ } = useTranslator();
  return (
    <Text weight="semibold">
      {priceText(formattedPrice, needsPhaseSelection, __)}
    </Text>
  );
}

function PriceWithDuration({
  minDuration,
  priceDiff,
  ...props
}: {
  formattedPrice: string,
  minDuration: number,
  needsPhaseSelection: boolean,
  priceDiff?: number,
}) {
  const { __ } = useTranslator();
  const durationLabel = durationText(minDuration, __, priceDiff);

  if (priceDiff) {
    return (
      <>
        {durationLabel} <NormalPrice {...props} />
      </>
    );
  }

  return (
    <>
      <NormalPrice {...props} /> {durationLabel}
    </>
  );
}

function PriceText({
  currency,
  price,
  minDuration,
  renewPrice,
  renewMinDuration,
  priceDiff,
  discountCheaperThanRenew,
  needsPhaseSelection,
  durationChange,
}: Props) {
  const { __, formatPrice } = useTranslator();
  const formattedPrice = formatPrice(price * minDuration, currency);
  const formattedRenewPrice =
    !!renewPrice &&
    !!renewMinDuration &&
    formatPrice(renewPrice * renewMinDuration, currency);

  const isLowerRenewPrice = Number(priceDiff) > 0 && !discountCheaperThanRenew;

  const durationChangeText = useChangeText({
    change: durationChange,
    duration: minDuration,
  });

  return (
    <Base className={styles.root}>
      <div>
        {
          // For a transfer, don't say "per year" unless a renew is included
          durationChange && durationChange === 'unchanged' ? (
            <NormalPrice
              formattedPrice={formattedPrice}
              needsPhaseSelection={needsPhaseSelection}
            />
          ) : (
            <PriceWithDuration
              formattedPrice={formattedPrice}
              minDuration={minDuration}
              needsPhaseSelection={needsPhaseSelection}
              priceDiff={priceDiff}
            />
          )
        }
      </div>
      {durationChangeText ? (
        <Text size="small" align="end" color="light">
          {durationChangeText}
        </Text>
      ) : (
        formattedRenewPrice &&
        formattedRenewPrice !== formattedPrice && (
          <Text
            size="small"
            align="end"
            color={isLowerRenewPrice ? 'promotional' : 'light'}
            weight={isLowerRenewPrice ? 'semibold' : undefined}
          >
            {' '}
            {__('then %(renewPrice)s %(renewDuration)s', {
              renewPrice: priceText(
                formattedRenewPrice,
                needsPhaseSelection,
                __,
              ),
              // $FlowFixMe - renewMinDuration cannot be undefined if formattedRenewPrice is not
              renewDuration: durationText(renewMinDuration, __),
            })}
          </Text>
        )
      )}
    </Base>
  );
}

export default (React.memo<Props>(PriceText): React.ComponentType<Props>);
